import { Check, Unlock } from "lucide-react"
import {
  BusinessTypeEnum,
  SubscriptionBillingCycleEnum,
} from "~/__generated__/graphql"
import { useSubscriptionTiers } from "~/common/useSubscriptionTiers"
import { Button } from "~/shadcn/ui/button"
import { Card, CardContent } from "~/shadcn/ui/card"
import { moneyDisplay } from "~/util/numbers"

// One destination for every business type (ZAN-893, confirmed with Sapir). This
// used to point at /pricing-for-brands, which was the wrong page even for the
// brands it was aimed at. Talent agencies belong here too: listing your own
// talent is free, and you upgrade when you want to search *other* agencies'
// talent for campaigns — i.e. when you start acting as a marketing agency. So
// the benefits copy below reads correctly for them as well.
const REQUEST_ACCESS_URL = "https://zanoplatform.com/pricing"

const MARKETPLACE_BENEFITS = [
  "Access everything in Casting",
  "Search 6,000 represented influencers",
  "Browse 370+ talent agency rosters",
  "Access marketplace insights & pricing benchmarks",
  "Consulting sessions",
  "Unlimited users",
  "Priority customer support",
]

// Shown overlaying the Dashboard, Roster Database and Talent Agencies pages when
// an agency is not on the Marketplace plan — brands/marketing since ZAN-854, and
// talent agencies on the free Roster plan since ZAN-893. The price is pulled live
// from the Marketplace tier so it stays in sync with Stripe. Marketplace is a
// brand/marketing tier, so it's always looked up as such: querying by the viewer's
// own business type returns no Marketplace tier for a talent agency, and the price
// silently disappears.
export const MarketplaceUpgradeCard = () => {
  const { subscriptionTiers } = useSubscriptionTiers(BusinessTypeEnum.Brand)

  // "enterprise" is the internal DB key for the Marketplace tier (the display
  // name was renamed Enterprise -> Marketplace; the key was intentionally kept).
  const marketplaceTier = subscriptionTiers.find(
    (tier) =>
      tier.key === "enterprise" &&
      tier.billingCycle === SubscriptionBillingCycleEnum.Monthly
  )

  return (
    <Card className="absolute top-1/2 left-1/2 z-10 w-[760px] max-w-[92%] -translate-x-1/2 -translate-y-1/2 bg-white drop-shadow-lg">
      <CardContent className="flex flex-col items-center gap-4 px-12 py-8 text-center">
        <Unlock className="h-7 w-7 text-black" strokeWidth={1.5} />
        <h2 className="text-2xl font-bold text-black">
          Uh oh! You don&apos;t have access yet
        </h2>
        <p className="text-base font-bold uppercase text-hoveredPrimary">
          Unlock the Marketplace plan
        </p>
        <p className="max-w-[620px] text-defaultSecondary">
          The Dashboard, Roster Database and Talent Agencies pages are part of
          the Marketplace plan which gives you full access to agency rosters,
          contact information, and marketplace insights.
        </p>

        <div className="flex w-full items-center justify-center gap-12 py-4 text-left">
          <div className="shrink-0">
            <p className="font-bold text-black">Marketplace Plan</p>
            {marketplaceTier && (
              <p className="text-[32px] font-bold leading-tight text-black">
                {moneyDisplay(marketplaceTier.priceCents / 100)}
                <span className="text-base font-normal"> / month</span>
              </p>
            )}
          </div>
          <div>
            <p className="mb-2">With marketplace, you&apos;ll be able to:</p>
            <ul className="flex flex-col gap-2">
              {MARKETPLACE_BENEFITS.map((benefit) => (
                <li key={benefit} className="flex items-center gap-2">
                  <Check
                    className="h-5 w-5 shrink-0 text-greenDark"
                    strokeWidth={2.5}
                  />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="max-w-[620px] text-xs text-black">
          This plan requires approval. We will review your request within 48
          hours. If approved, you will be able to upgrade within Stripe.
        </p>
        <a
          href={REQUEST_ACCESS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full"
        >
          <Button className="w-full">Request Access</Button>
        </a>
      </CardContent>
    </Card>
  )
}
